import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../../common/LoadingSpinner';
import AdaptiveProgress from '../../common/AdaptiveProgress';
import { useTranslation } from '../../../hooks/useTranslation';
import { FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import styles from './LoadingOverlay.module.css';
import { detectBrowser } from '../../../utils/browserUtils';

interface LoadingOverlayProps {
    isError?: boolean;
    onRetry?: () => void;
    useAdaptiveProgress?: boolean;
    startTime?: number;
    isProcessing?: boolean;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ 
    isError = false, 
    onRetry,
    useAdaptiveProgress = true,
    startTime,
    isProcessing = true
}) => {
    console.log('🔥 LoadingOverlay rendered with props:', { isError, useAdaptiveProgress, startTime, isProcessing });
    
    const [loadingMessage, setLoadingMessage] = useState('upload.loading.processing');
    const { t } = useTranslation();
    const browser = detectBrowser();
    const [showSafariTip, setShowSafariTip] = useState(false);

    useEffect(() => {
        const messages = [
            'upload.loading.processing',
            'upload.loading.extracting',
            'upload.loading.analyzing',
            'upload.loading.mapping',
            'upload.loading.schema',
            'upload.loading.validating'
        ];

        let currentIndex = 0;
        const messageInterval = setInterval(() => {
            currentIndex = (currentIndex + 1) % messages.length;
            setLoadingMessage(messages[currentIndex]);
        }, browser.isSafari ? 3000 : 2500); // Slower for Safari

        // Show Safari tip after 30 seconds if using Safari
        let safariTipTimeout: NodeJS.Timeout;
        if (browser.isSafari) {
            safariTipTimeout = setTimeout(() => {
                setShowSafariTip(true);
            }, 30000);
        }

        return () => {
            clearInterval(messageInterval);
            if (safariTipTimeout) {
                clearTimeout(safariTipTimeout);
            }
        };
    }, [browser.isSafari]);

    return (
        <div className="absolute inset-0 rounded-xl bg-bg-primary/60 backdrop-blur-sm flex items-center justify-center z-20">
            <div className="flex flex-col items-center gap-4">
                {isError ? (
                    <>
                        <FaExclamationTriangle size={48} className="text-[var(--error)] animate-pulse" />
                        <p className="text-[var(--error)] text-lg">{t('upload.errors.processingFailed')}</p>
                        {onRetry && (
                            <button
                                onClick={onRetry}
                                className="mt-2 px-4 py-2 bg-[var(--error)] hover:bg-[var(--error)]/80 text-white rounded transition-colors"
                            >
                                <FaRedo className="inline-block mr-2" /> {t('actions.retry') || 'Retry'}
                            </button>
                        )}
                    </>
                ) : (
                    <>
                        {useAdaptiveProgress ? (
                            <AdaptiveProgress
                                isProcessing={isProcessing}
                                startTime={startTime}
                                size="lg"
                                showPercentage={true}
                                showMessages={true}
                            />
                        ) : (
                            <>
                                <LoadingSpinner size="lg" color="var(--brand-primary)" />
                                <p className={`text-primary text-lg ${styles['joyful-text']}`}>
                                    {t(loadingMessage)}
                                </p>
                            </>
                        )}
                        {browser.isSafari && showSafariTip && (
                            <div className="mt-4 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg border border-warning dark:border-warning max-w-md text-center">
                                <p className="text-sm text-warning dark:text-warning">
                                    <strong>Safari Note:</strong> Processing may take longer in Safari. 
                                    Keep this tab active for best performance. If it's stuck, try refreshing or using Chrome/Firefox.
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default LoadingOverlay;